/**
 * O CADERNO DE BORDO DAS NOTIFICAÇÕES NO APLICATIVO.
 *
 * Dentro do app instalado não há console para olhar: quem testa é o Leandro,
 * no celular dele, e o que chega até aqui é "continua com o problema". Três
 * entregas seguidas tentaram adivinhar qual ponta não responde — a permissão
 * do Android, o Firebase, o servidor — e cada uma errou o alvo porque o
 * código engolia o erro de verdade e mostrava só um aviso genérico.
 *
 * Então o caminho passou a ANOTAR o que acontece, em português, com a hora:
 * cada pergunta feita ao aparelho, o que ele respondeu e, quando falha, a
 * mensagem crua do erro. A tela de Notificações mostra isso em "Detalhes
 * técnicos", com um botão de copiar — é o que o relato precisa trazer para a
 * correção parar de ser chute.
 *
 * Fica na memória da página (nada é gravado em lugar nenhum) e guarda as
 * últimas linhas. É só diagnóstico: nada aqui muda o que o sistema faz.
 */

import { BUILD_SHA } from "@/lib/ui/build-sha";

const LIMITE = 40;

type Linha = { hora: string; texto: string };

const linhas: Linha[] = [];

function agora(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Anota um fato do caminho das notificações. */
export function anotar(texto: string): void {
  linhas.push({ hora: agora(), texto });
  if (linhas.length > LIMITE) linhas.splice(0, linhas.length - LIMITE);
}

/** A mensagem crua de um erro, do jeito que ela vier. */
export function motivo(e: unknown): string {
  if (e instanceof Error) return e.message || e.name;
  if (typeof e === "string") return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

/** O que a casca do app diz de si mesma — a primeira coisa a conferir. */
export function resumoDoAparelho(): string[] {
  if (typeof window === "undefined") return [];
  const cap = (
    window as unknown as {
      Capacitor?: {
        getPlatform?: () => string;
        isNativePlatform?: () => boolean;
        Plugins?: Record<string, unknown>;
      };
    }
  ).Capacitor;
  const codigo = `Código: ${BUILD_SHA || "local"}`;
  if (!cap) return [codigo, "Capacitor: ausente (navegador comum)"];
  const plugins = cap.Plugins ? Object.keys(cap.Plugins) : [];
  return [
    codigo,
    `Plataforma: ${cap.getPlatform?.() ?? "?"} · app instalado: ${
      cap.isNativePlatform?.() === true ? "sim" : "não"
    }`,
    `Plugins na ponte: ${plugins.length ? plugins.join(", ") : "nenhum"}`,
  ];
}

/** As linhas anotadas até agora, da mais antiga para a mais nova. */
export function diagnostico(): string[] {
  return linhas.map((l) => `${l.hora} ${l.texto}`);
}

/** Tudo junto, para copiar e colar no relato. */
export function diagnosticoParaCopiar(): string {
  return [...resumoDoAparelho(), ...diagnostico()].join("\n");
}
